import {
  Autocomplete,
  TextField,
  createFilterOptions,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { useMenuDataCache } from '../../hooks/useMenuDataCache';

interface Product {
  productId: string;
  internalName: string;
  name?: string;
  [key: string]: any;
}

interface ProductSelectProps {
  value: Product | null | Product[];
  onChange: (product: Product | null | Product[]) => void;
  multiple?: boolean;
  onProductChange?: () => void; // Optional callback for when product changes
}

export function ProductSelect({
  value,
  onChange,
  multiple = false,
  onProductChange,
}: ProductSelectProps) {
  // Track component mount status to prevent memory leaks
  const isMountedRef = useRef(true);
  
  // Use singleton cached menu data - this will only fetch once across all instances
  const { data: menuData, isLoading: productsLoading, fetchMenuData } = useMenuDataCache();

  useEffect(() => {
    // Trigger cache loading - singleton pattern ensures this only fetches once
    fetchMenuData();
    
    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchMenuData]);

  // Get products from cache
  const products = menuData?.products || [];

  // 2) compute a “safe” value for Autocomplete
  const safeValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : (value as Product | null) ?? null;

  // 3) your existing filterOptions
  const filterOptions = createFilterOptions<Product>({
    matchFrom: 'any',
    stringify: (option) =>
      `${option.name || option.internalName || ''} ${
        option.productCode || ''
      }`,
  });

  return (
    <Autocomplete
      options={Array.isArray(products) ? products : []}
      multiple={multiple}
      limitTags={2}
      filterOptions={filterOptions}
      filterSelectedOptions

      // USE our guarded value, never `null` when multiple
      value={safeValue}
      onChange={(_, newValue) => {
        onChange(newValue);
        // Call the onProductChange callback if provided
        if (onProductChange) {
          onProductChange();
        }
      }}

      isOptionEqualToValue={(option, val) =>
        !!option && !!val && option.productId === (val as Product).productId
      }

      renderOption={(props, option) => (
        <li {...props} key={option.productId}>
          {`${option.name || option.internalName || 'Unknown'} (${
            option.productCode || 'N/A'
          })`}
        </li>
      )}

      renderTags={(tagValue, getTagProps) =>
        Array.isArray(tagValue)
          ? tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.productId}
                size="small"
                label={option.name || option.internalName || 'Unknown'}
              />
            ))
          : []
      }

      getOptionLabel={(option) =>
        option
          ? `${option.name || option.internalName || 'Unknown'} (${
              option.productCode || 'N/A'
            })`
          : ''
      }

      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Product"
          placeholder="Search for products..."
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {productsLoading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
